import { QueryClient } from '@tanstack/react-query'
import { Dispatch } from '@reduxjs/toolkit'
import io, { Socket as SocketClient } from 'socket.io-client'
import i18n from 'i18next'

import Toast from 'components/Toast'

import {
  LeadNameChangedData,
  LeadOwnerChangedData,
  QuotationChangedStatus,
  LeadProcessStageAddedData,
  LeadProcessStageRemovedData,
} from 'services/types/notification'
import { Auth, Notification } from 'store'
import { leadKey, leadLayoutKey } from 'utils/constants/queryKey'
import { getUserDetail, getUserPermission } from 'services/user'
import { getUnReadNotificationCount } from 'services/notification'
import { getHostname, getLeadRoute } from 'utils/url'

import { SocketEvents } from './events'
import * as SocketTypes from './types'

const { toastMessage } = Toast

class Socket {
  private _socket: SocketClient | null = null

  public disconnect() {
    this._socket?.disconnect()
    this._socket = null
  }

  public init(_masterId: string, userId: string, dispatch: Dispatch<any>, queryClient: QueryClient) {
    // Prevent re-initialize the connection

    if (this._socket) return

    this._socket = io(process.env.SOCKET_HOST || getHostname(), {
      path: process.env.SOCKET_PATH || '/socket/socket.io',
      query: { userId },
      transports: ['websocket'],
    })

    // Disable reminders window for now
    // this._socket.on(SocketEvents.DISMISS_REMINDER_ACTIVITY, (data: RemoveReminder) => {
    //   dispatch(Reminder.removeReminder(data))
    // })

    this._socket.on(SocketEvents.LEAD_NAME_UPDATE, ({ data }: { data: LeadNameChangedData }) => {
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.LEAD_NAME_UPDATE, data }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.LEAD_OWNER_UPDATE, ({ data }) => {
      const payload = data as LeadOwnerChangedData
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.LEAD_OWNER_UPDATE, data: payload }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.LEAD_PROCESS_UPDATE, ({ data }: { data: LeadNameChangedData }) => {
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.LEAD_PROCESS_UPDATE, data }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.LEAD_STATUS_UPDATE, ({ data }: { data: LeadNameChangedData }) => {
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.LEAD_STATUS_UPDATE, data }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.QUOTATION_STATUS_UPDATE, ({ data }: { data: QuotationChangedStatus }) => {
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.QUOTATION_STATUS_UPDATE, data }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.AUTO_REFRESH_LEAD_PAGE, ({ data }: { data: { lead_id: string } }) => {
      if (window.location.href.includes(getLeadRoute({ leadId: data.lead_id }))) {
        queryClient.invalidateQueries(leadKey.data(data.lead_id))
        queryClient.invalidateQueries(leadKey.detail(data.lead_id))
        toastMessage({
          message: i18n.t('refetching_data', {
            name: i18n.t('lead_details_lbl'),
          }),
          variant: 'info',
        })
      }
    })

    this._socket.on(SocketEvents.LEAD_DETAIL_PAGE_LAYOUT_UPDATE, () => {
      queryClient.invalidateQueries(leadLayoutKey.detail())
      if (window.location.href.includes('/lead/')) {
        toastMessage({
          message: i18n.t('lead_details_page_layout_update_msg'),
          variant: 'info',
        })
      }
    })

    //TODO: Re-implement OBJECT_UPDATE_TIMESTAMP with proper test
    // this._socket.on(SocketEvents.OBJECT_UPDATE_TIMESTAMP, ({ name }: { name: keyof typeof OBJECT_CONTROL_KEYS }) => {
    //   switch (name) {
    //     case 'app_settings':
    //     case 'master_settings':
    //       queryClient.invalidateQueries(appSettingsKey.details())
    //       break
    //     case 'address_field':
    //       dispatch(getCustomAddressConfiguration())
    //       break
    //     case 'branch_settings':
    //     case 'translation':
    //       // TODO: Include these with below case once mapping is available
    //       break
    //     default:
    //       // Proceed with refetching lookup only if socket key is supported
    //       if (OBJECT_CONTROL_KEYS[name]) {
    //         const fields = [OBJECT_CONTROL_KEYS[name] as keyof typeof LOOKUP_FIELDS]

    //         // Refetch lead process too when lead status is updated
    //         if (name === 'lead_status') {
    //           fields.push(LOOKUP_FIELDS.LEAD_PROCESS)
    //         }

    //         queryClient.invalidateQueries(lookupKey.lookups())
    //       }
    //   }
    // })

    // Disable reminders window for now
    // this._socket.on(SocketEvents.PUSH_REMINDER_ACTIVITY, (data: LeadActivityReminder) => {
    //   if (store.getState().user.selectedBranch?.branchId === data.branch_id) {
    //     dispatch(Reminder.addReminder(data))
    //   }
    // })

    this._socket.on(SocketEvents.UPDATE_TOKEN, (data: SocketTypes.UpdateAuthResponse) => {
      dispatch(Auth.updateAuth(data.auth_token))
      dispatch(getUserDetail({ user_id: userId }))
      dispatch(getUserPermission())
    })
    this._socket.on(SocketEvents.WORKFLOW_PROCESS_STAGE_DELETE, ({ data }: { data: LeadProcessStageRemovedData }) => {
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.WORKFLOW_PROCESS_STAGE_DELETE, data }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.WORKFLOW_PROCESS_STAGE_ADD, ({ data }: { data: LeadProcessStageAddedData }) => {
      dispatch(Notification.appendNotification({ notificationType: SocketEvents.WORKFLOW_PROCESS_STAGE_ADD, data }))
      dispatch(getUnReadNotificationCount())
    })
    this._socket.on(SocketEvents.PUSH_PERMISSION_CONTROL, () => {
      dispatch(getUserPermission())
    })
  }

  public addListener(event: SocketEvents, listener: (data: any) => void) {
    this._socket?.on(event, listener)
  }

  public removeListener(event: SocketEvents, listener: (data: any) => void) {
    this._socket?.off(event, listener)
  }
}

const socket = new Socket()

export { socket, SocketEvents, SocketTypes }
